import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Appearance: colour scheme and text size.
 *
 * Both are device preferences rather than account settings — the same person
 * may want a dark screen on their own phone and not on the tablet in the
 * office — so they live in localStorage and never touch the server.
 *
 * The initial values are applied by a small inline script in index.html before
 * first paint. Doing it here instead would show a white screen for a frame on
 * every launch, which is exactly the moment dark mode is supposed to prevent.
 */

export type ThemePref = 'system' | 'light' | 'dark';
export type TextSize = 'default' | 'large' | 'larger';

export const TEXT_SCALE: Record<TextSize, number> = {
  default: 1,
  large: 1.12,
  larger: 1.25,
};

export const TEXT_SIZE_LABEL: Record<TextSize, string> = {
  default: 'Default',
  large: 'Large',
  larger: 'Larger',
};

const THEME_KEY = 'huddle:theme';
const SIZE_KEY = 'huddle:text-size';

/** Matches the status bar to the page so the notch area does not stay white. */
const THEME_COLOR = { light: '#0d9488', dark: '#101317' };

export function readThemePref(): ThemePref {
  const stored = localStorage.getItem(THEME_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

export function readTextSize(): TextSize {
  const stored = localStorage.getItem(SIZE_KEY);
  return stored === 'large' || stored === 'larger' ? stored : 'default';
}

/** Resolve 'system' against the OS setting. */
export function resolveTheme(pref: ThemePref): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyAppearance(pref: ThemePref, size: TextSize): void {
  const resolved = resolveTheme(pref);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.setProperty('--font-scale', String(TEXT_SCALE[size]));
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLOR[resolved]);
}

interface AppearanceValue {
  theme: ThemePref;
  /** What 'system' actually resolved to right now. */
  resolved: 'light' | 'dark';
  textSize: TextSize;
  setTheme: (next: ThemePref) => void;
  setTextSize: (next: TextSize) => void;
}

const AppearanceContext = createContext<AppearanceValue | null>(null);

export function useAppearance(): AppearanceValue {
  const value = useContext(AppearanceContext);
  if (!value) throw new Error('useAppearance must be used inside <AppearanceProvider>');
  return value;
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePref>(() => readThemePref());
  const [textSize, setTextSizeState] = useState<TextSize>(() => readTextSize());
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(readThemePref()));

  const setTheme = useCallback((next: ThemePref) => {
    localStorage.setItem(THEME_KEY, next);
    setThemeState(next);
    setResolved(resolveTheme(next));
    applyAppearance(next, readTextSize());
  }, []);

  const setTextSize = useCallback((next: TextSize) => {
    localStorage.setItem(SIZE_KEY, next);
    setTextSizeState(next);
    applyAppearance(readThemePref(), next);
  }, []);

  // Follow the phone when it switches at sunset, but only while the preference
  // is actually 'system'.
  useEffect(() => {
    if (theme !== 'system') return;
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return;

    const onChange = () => {
      setResolved(resolveTheme('system'));
      applyAppearance('system', readTextSize());
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, resolved, textSize, setTheme, setTextSize }),
    [theme, resolved, textSize, setTheme, setTextSize],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}
